import { useEffect, useState } from 'react'
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { Send } from 'lucide-react'
import { TicketService } from '@/lib/services/ticket'
import type { TicketUpdate } from '@/types/parentTicket'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useToastNotification } from '@/lib/utils/useToastNotification'

type Props = {
  isOpen: boolean
  onClose: () => void
  ticketId: string
  ticketNumber?: string
  onSubmitted?: () => void
  editingUpdate?: TicketUpdate | null
}

export default function TicketUpdateModal({ isOpen, onClose, ticketId, ticketNumber, onSubmitted, editingUpdate }: Props) {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const [content, setContent] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const isEditing = editingUpdate != null

  useEffect(() => {
    if (isOpen) {
      setContent(editingUpdate?.content ?? '')
    }
  }, [isOpen, editingUpdate])

  const handleSubmit = async () => {
    const trimmed = content.trim()
    if (!trimmed) return

    setSubmitting(true)

    const res = isEditing
      ? await TicketService.editTicketUpdate(ticketId, editingUpdate!.id, {
          content: trimmed,
        })
      : await TicketService.createTicketUpdate(ticketId, {
          content: trimmed,
          type: 'comment',
          is_internal: true,
        })

    setSubmitting(false)

    if (res.success) {
      showSuccess(isEditing ? 'Update modified' : 'Update submitted')
      setContent('')
      onSubmitted?.()
      onClose()
    } else {
      showError(res.message || 'Failed to submit update')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent>
        <ModalHeader pb={2}>
          <Text fontWeight="700" color={colors.primary}>
            {isEditing ? 'Edit Update' : 'Ticket Updates'}
          </Text>
          <Text fontSize="xs" color="gray.500" fontWeight="400" mt={0.5}>
            {ticketNumber || `Ticket #${ticketId}`}
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={5}>
          <VStack spacing={3} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                {isEditing ? 'Edit Update' : 'Add Update'}
              </FormLabel>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your update here... (Ctrl+Enter to submit)"
                size="sm"
                rows={3}
                resize="vertical"
                borderColor="gray.200"
                _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
              />
            </FormControl>
            <HStack justify="flex-end">
              <Button
                leftIcon={<Send size={14} />}
                colorScheme="blue"
                size="sm"
                onClick={handleSubmit}
                isLoading={submitting}
                isDisabled={!content.trim()}
              >
                {isEditing ? 'Save' : 'Submit'}
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
